import { AssetFilter } from 'app/lib/constants';
import { SECRET_NOT_FOUND, SNOWFLAKE_INTERNAL_SERVER_ERROR, UNKNOWN_COLUMN_NAMES, UNKNOWN_COLUMN_NAMES_WITH_SOURCE, DUPLICATE_COLUMN_NAME, MISSING_SOURCE_COLUMN, COLUMN_REMOVED_UPSTREAM, EXPECTED_COLUMN_MISSING, TYPES_NOT_COERCIBLE, TYPE_COERCION, TRANSFORMATION_OUT_OF_SYNC, COLUMN_ADDED_UPSTREAM, MODEL_NOT_RESOLVED, MODEL_NOT_CONNECTED, HAS_DRAFT_MODEL_UPSTREAM, UNEXPECTED_COLUMN, MATERIALIZATION_CANNOT_BE_DELETED, MULTI_SOURCE_CANNOT_BE_DELETED, SOURCE_CANNOT_BE_DELETED } from 'app/store/__generated__/errors';

export const translation = {
    AcceptEulaDialog: {
        accept: 'Accept',
        eula: 'End User License Agreement',
        privacy: '<externalLink>Privacy Policy</externalLink> <small>(External Link)</small>'
    },
    AddCollaboratorsDialog: {
        title: 'Collaborators',
        filterInputPlaceholder: 'Find people…',
        subHeader: 'Add more people…',
        countText: '{{ assignedItemCount }} of {{ totalItemCount }} people added',
        noRemainingUnassignedItems: 'All people are already collaborating on this $t(glossary:project).',
        noItemsMatchingFilter: 'Unfortunately, there is no one who matches <b>{{ filterValue }}</b>',
        noAssignedItems: 'No user added',
        owner: '(Owner)',
        pending: '(Pending)',
        makeOwner: 'Make {{- username}} the Owner',
        collaborator_add: 'Add {{- username}} to $t(glossary:project)',
        collaborator_remove: 'Remove {{- username}} from $t(glossary:project)'
    },
    AddSchemaConfirmationDialog: {
        title: 'Add {{count}} datasets',
        message: "Are you sure you want to add all {{count}} datasets from '{{database}}.{{schema}}'?",
        submit: 'Add all Datasets',
        noSources: {
            title: 'No datasets found',
            message: "We couldn't find any datasets to add in '{{database}}.{{schema}}'."
        }
    },
    AddToProjectDialog: {
        title: 'Add to $t(glossary:project)',
        placeholder: 'Find $t(glossary:project)…',
        alreadyAdded: 'Already added',
        noProjectsAvailable: '<0>It appears you do not have $t(glossary:project_other) available.</0><1>Select <strong>New</strong> below to create a new $t(glossary:project).</1>',
        includeExplorations: 'Include explorations'
    },
    AssetBrowser: {
        title: '$t(common:dataBrowser)',
        actions: {
            insertTableName: 'Insert Table Name at Cursor',
            insertColumnName: 'Insert Column Name at Cursor',
            previewDataset: 'Preview Dataset'
        },
        filter: 'Find data…'
    },
    AssetNameInput: {
        label: 'New Asset Name',
        hint: "Names must not start with a whitespace, an underscore '_' or contain backticks '`'."
    },
    AssetReferencesMetaItem: {
        project_one: '{{count}} $t(glossary:project)',
        project_other: '{{count}} $t(glossary:project_other)',
        dataset_one: '{{count}} $t(glossary:dataset)',
        dataset_other: '{{count}} $t(glossary:dataset_other)'
    },
    AssetUsageDialog: {
        queryCount: 'query count',
        recordsCount: 'records count',
        queryTime: 'query time in ms',
        updateTime: 'update time in ms',
        writeVsReadScore: 'Write vs Read: {{value}}',
        queryTimeVsCountScore: 'Query Time vs Count: {{value}}'
    },
    Avatar: {
        deactivatedTitle: '{{- user}} (deactivated)'
    },
    BlendBrandBar: {
        title: 'Join Configuration'
    },
    BlendSidebar: {
        sourcePanelTitle: 'Choose Sources',
        sourcePanelModeTitle: 'Choose Join Mode',
        columnPanelTitle: 'Choose Columns',
        selectDataset: 'Select dataset…',
        closeButtonLabel: 'Clear selection',
        dedupeJoinKeys: 'Include join key columns from both sources',
        dedupe: 'When checked, includes the join keys from the RHS table duplicating the keys. This can be useful in OUTER JOIN’s to determine matches downstream.'
    },
    BlendTypes: {
        INNER: {
            label: 'inner join',
            tooltip: 'Returns only matching data from both datasets'
        },
        LEFT_OUTER: {
            label: 'outer left',
            tooltip: 'Returns data from the starting dataset and matching data from the second dataset'
        },
        RIGHT_OUTER: {
            label: 'outer right',
            tooltip: 'Returns data from the second dataset and matching data from the starting dataset'
        },
        FULL_OUTER: {
            label: 'full outer join',
            tooltip: 'Returns all data when there is a match in either dataset'
        }
    },
    BrandBar: {
        img: '$t(common:productName) Logo'
    },
    BrowseFacetValuesDialog: {
        searchForValues: 'Search for facet values…'
    },
    BuildInfo: {
        title: '$t(common:productName) Build Info',
        empty: 'Sorry, no build information found'
    },
    CancelAllQueriesConfirmationDialog: {
        warning: 'This will cancel all running Snowflake queries associated with your session. Are you sure you want to proceed?',
        confirm: 'Cancel All Queries',
        hint: 'Press & Hold to Confirm'
    },
    CellDetailsDialog: {
        hintMessage: 'Click on a row to see the selector for the path',
        pathLabel: 'Path for:',
        insertTooltip: 'Insert path expression into editor at current cursor position',
        copyPathTooltip: 'Copy path expression to clipboard',
        copyTooltip_json: '$t(common:copyTooltip_json)',
        copyTooltip_text: '$t(common:copyTooltip_text)',
        truncatedWarning: 'The original value of this cell was too large and has been truncated to a <type>String</type> preview of the first <b>{{limit}}</b> characters.',
        extractButton: 'Extract to Dataset',
        hasArrayIndex: 'Array elements cannot be extracted'
    },
    CharacterCount: {
        message: '{{limit}} characters max.',
        message_under_one: '{{count}} character left.',
        message_under_other: '{{count}} characters left.',
        message_over_one: '{{count}} character over limit!',
        message_over_other: '{{count}} characters over limit!'
    },
    CheckboxList: {
        selectAll: '$t(common:selectAll)',
        search: 'Search…',
        totalCount: '{{count}} total',
        selectedCount: '{{selectedCount}} of {{count}} selected',
        showAll: 'Show All',
        showSelected: 'Show Selected'
    },
    CloneProjectDialog: {
        title: '$t(common:cloneProject)',
        inputLabel: 'Name',
        initialName: 'Clone of {{- name }}'
    },
    CodeEditor: {
        run: 'Run Query',
        cancel: 'Cancel Query',
        undo: 'Undo',
        redo: 'Redo',
        discardChanges: 'Discard Changes',
        format: 'Format',
        formatMacTip: 'Format ⇧-⌘-f',
        formatTip: 'Format ⇧-^-f',
        formatError: 'Cannot format due to a syntax error in your SQL.'
    },
    ColumnCount: {
        message_default: 'Showing {{total}} columns',
        message_filtered: 'Showing {{visible}} of {{total}} columns'
    },
    ColumnFilter: {
        placeholder: 'Filter columns…',
        showCalculated: 'Show Only Calculated'
    },
    ColumnMappingGrid: {
        notIncluded: 'Not included',
        noSource: 'Source does not exist'
    },
    ColumnMetrics: {
        calculate: 'Calculate',
        start: 'Start',
        end: 'End',
        min: 'Min',
        avg: 'Avg',
        max: 'Max',
        confidence: 'Score',
        values: 'Values',
        empty: 'Empty',
        null: '$t(common:null)',
        unique: 'Unique',
        interval: 'Interval',
        value: 'Value',
        count: 'Count',
        leadingWhitespace: 'Leading',
        trailingWhitespace: 'Trailing',
        surroundingWhitespace: 'Surrounding',
        whitespace: 'Whitespace',
        lengths: 'Lengths',
        messages: {
            allUnique: 'All values in this column are unique',
            highCardinality: 'This column has high cardinality'
        }
    },
    ColumnNameInput: {
        label: 'New Column Name',
        hint: 'Column names can only contain letters, numbers, underscores and white spaces'
    },
    ColumnTooltip: {
        emptyState: 'No metadata defined for this column. You can add a description under the "Columns" tab or find metadata for the dataset as a whole in the "Info" inspector'
    },
    ConfigureOptions: {
        addOption: {
            label: 'Enter value for new option',
            placeholder: 'Enter value…',
            action: 'Add Option'
        },
        editOption: {
            label: 'Edit value for option {{number}}',
            action: "Delete value '{{value}}'"
        }
    },
    ConvertAllDeploymentsDialog: {
        title: 'Convert Deployments to Models',
        disabled: '$t(common:disabled)',
        disabledReason: 'There are no deployment nodes in this project.',
        banner: 'Converts all deployment nodes in this project to models with the same role and warehouse as specified by the project environment.',
        environment: 'Environment',
        rolewh: 'Role + Warehouse',
        valid: 'Valid',
        mismatched: 'Mismatched Warehouse',
        pending: 'Pending',
        header: {
            started: '<b>Conversion in Progress:</b> Completed {{completed}} of {{total}}.',
            completed: '<b>Conversion Completed: {{completed}}</b> deployments were successfully converted to models.',
            failed: 'Failed to convert <b>{{name}}</b>.'
        },
        columns: {
            status: '$t(common:status)',
            name: '$t(common:name)',
            schema: '$t(glossary:schema)',
            role: '$t(glossary:role)',
            warehouse: '$t(glossary:warehouse)'
        },
        status: {
            pending: 'Pending',
            started: 'Converting to Model',
            succeeded: 'Model Conversion Succeeded',
            failed: 'Model Conversion Failed'
        },
        actions: {
            cancel: '$t(common:cancel)',
            close: '$t(common:close)',
            convert: 'Convert to Models'
        }
    },
    CopyToClipboardButton: {
        label: 'Copy to Clipboard'
    },
    CreateProjectDialog: {
        title: 'Create New $t(glossary:project)',
        inputLabel: 'Name'
    },
    CustomCriteria: {
        selectColumn: 'Select Column…'
    },
    DataDrivenConfig: {
        title: 'Data-Driven',
        config: 'Triggered by updates to',
        triggered: 'Triggered',
        triggeredBy: 'Triggered by',
        createdIn: 'Created in',
        byUser: 'Ad-hoc by user',
        byApi: 'Externally by API',
        disabled: 'Only tables <strong>managed by an existing Datameer project</strong> may be chosen as trigger source.',
        noJob: 'Only tables <strong>associated with Jobs</strong> may be chosen as trigger source.'
    },
    DataGridErrorBoundaryMessage: {
        title: 'Oops, something went wrong',
        message: '<p><b>You have encountered this error:</b> <code>{{- message }}</code></p>'
    },
    DataQualityCheckDialog: {
        title_create: 'Add Data Quality Check',
        title_update: 'Edit Data Quality Check',
        type: {
            label: 'Target',
            options: {
                model: 'Model',
                source: 'Source Table'
            }
        },
        target: {
            label: 'Source Table',
            placeholder: 'Select a source table…',
            errors: {
                required: 'Source Table is required.'
            }
        },
        checkName: {
            label: 'Check Name',
            placeholder: 'Select a data quality check…',
            options: {
                ExpectDateWithinPeriod: 'Dataset Freshness',
                ExpectAggregateWithinRange: 'Aggregate in Range',
                ExpectColumnNotNull: 'Column is Not Null',
                ExpectAllValuesWithinRange: 'All Values in Range',
                ExpectValuesMatchSet: 'Values Match Set',
                ExpectRowCountWithinRange: 'Row Count in Range',
                ExpectUniqueColumnValues: 'Unique Column Values'
            }
        },
        column: {
            label: 'Column',
            placeholder: 'Select a column…',
            errors: {
                required: 'Column is required.'
            }
        },
        datetime: {
            label: 'Column (DATETIME)',
            placeholder: 'Select a column…'
        },
        func: {
            label: 'Aggregation Function',
            placeholder: 'Select an aggregation function…'
        },
        method: {
            label: 'Match Method',
            placeholder: 'Select a method…',
            errors: {
                required: 'Match Method is required.'
            }
        },
        period: {
            label: 'Period',
            errors: {
                required: 'Period is required.',
                positive: 'Period must be a positive number.'
            }
        },
        unit: {
            label: 'Unit'
        },
        min: {
            label: 'Minimum (Inclusive)',
            errors: {
                invalid: 'Invalid input.',
                positive: 'Minimum value must be a positive number.',
                required: 'Minimum value is required.'
            }
        },
        max: {
            label: 'Maximum (Inclusive)',
            errors: {
                invalid: 'Invalid input.',
                required: 'Maximum value is required.',
                range: 'Maximum value must be greater than or equal to the minimum value.'
            }
        },
        values: {
            label: 'Values',
            placeholder: 'Enter a list of comma separated values…',
            errors: {
                minimum: 'At least 1 value is required.'
            }
        },
        actions: {
            cancel: 'Cancel',
            ok_create: 'Create',
            ok_update: 'Update'
        }
    },
    DatasetTabs: {
        tooltips: {
            invalid: 'Data is invalid due to upstream changes. Refresh the query to get the latest results.'
        }
    },
    DatasizeInfo: {
        noCount: '-- Rows',
        noCount_small: '--',
        rowCount: "{{ recordCount, number }} $t(glossary:row, {'count': {{recordCount}} })",
        rowCount_small: '{{ recordCount, number }}',
        sampledData: 'Large dataset. Previews referencing this dataset will be sampled down to {{sampleSize, number}} records ',
        sampledSources: 'Some sources use sampled data: {{- sampledInputTables, list }}',
        calculate: 'Calculate Row Count',
        cancel: 'Cancel Calculation'
    },
    DatasourceDetailsInspector: {
        deletedProjectOrigin: 'Deleted Datameer Project'
    },
    DeleteDialog: {
        hint: 'Press & Hold to Delete!'
    },
    DeleteProjectDialog: {
        title: '$t(common:areYouSure)',
        message: "This will permanently delete the $t(glossary:project) '{{- name}}' and all the transformations it contains. Deployments will not be affected. Schedules will be stopped and data-driven triggers will be removed."
    },
    DeleteSnowflakeTableDialog: {
        title: 'Delete Snowflake Asset',
        title_table: 'Delete Snowflake Table',
        title_view: 'Delete Snowflake View',
        prompt: 'Are you sure you want to <strong>permanently delete</strong> "{{name}}" from Snowflake?',
        breakage_zero: 'This will not break any projects.',
        breakage_one: 'This will <strong>break {{count}} project</strong> using this dataset.',
        breakage_other: 'This will <strong>break {{count}} projects</strong> using this dataset.',
        confirmation: {
            label: '$t(common:deleteFromSnowflake)'
        }
    },
    DestinationTree: {
        hint_TABLE: 'Select a schema with sufficient privileges to create a table.',
        hint_VIEW: 'Select a schema with sufficient privileges to create a view.',
        placeholder: 'Filter schemas…',
        documentation: 'You did not find the schema you were looking for?',
        emptySearchResults: 'The search returned no results.'
    },
    EmptyPreview: {
        previewUnavailable: 'Preview Unavailable',
        emptyPreviewMessage: 'One or more errors have impacted this dataset. Preview capabilities are not available while there are unresolved errors.',
        emptyCanceledPreview: 'Preview is not available because the request was canceled.',
        reload: 'Do you want to <reload />?'
    },
    EntityDescription: {
        placeholder: 'Please provide some information',
        add: 'Add Description',
        placeholder_readOnly: 'Unfortunately, nobody added a description yet',
        error: 'Failed to update description'
    },
    ErrorDetailsDialog: {
        error: 'Error',
        operationFailed: 'Operation failed'
    },
    ExecutionTriggerConfigurationDialog: {
        title: 'Execution Trigger Configuration',
        api: {
            title: 'API',
            config: 'With completion status posted to',
            callbackUrl: 'Callback URL',
            callbackUrlOptional: 'Callback URL (Optional)',
            submitMethod: 'Submit Method',
            submitUrl: 'Submit URL',
            copyCallbackTooltip: 'Copy Callback URL to clipboard',
            copyCallbackSuccess: 'Copied Callback URL to clipboard',
            copySubmitTooltip: 'Copy Submit URL to clipboard',
            copySubmitSuccess: 'Copied Submit URL to clipboard'
        }
    },
    Exploration: {
        new_exploration: 'New Exploration…',
        name_one: 'Explore',
        name_other: 'Exploration {{count}}',
        title: 'Explore Results',
        title_error: 'You have to fix all errors in the transformation before you can start a new exploration',
        edit: 'Edit exploration',
        remove: 'Remove exploration tab',
        refresh: 'Refresh exploration tab',
        convert: 'Add exploration to workbench',
        empty: 'Exploration Unavailable',
        menu: 'Open exploration menu',
        dropdown: '{{quantity}} more explorations',
        oldVersionMessage: "We've upgraded your experience. Unfortunately, our new version requires you to recreate this exploration.",
        outOfSync: 'Exploration Results are out of sync, please refresh the query',
        noResults: {
            sharing: 'Exploration query returned no results. Sharing disabled.',
            download: 'Exploration query returned no results. Download disabled.'
        },
        deleteDialog: {
            title: '$t(common:areYouSure)',
            body: "This will permanently delete '{{tabName}}'.",
            submitBtn: '$t(common:delete)'
        }
    },
    FacetMenu: {
        searchValuePlaceholder: 'Type value to find…',
        browseAllValuesButton: 'Browse all values'
    },
    FacetTagsList: {
        facetsCount_one: '{{count}} Facet applied:',
        facetsCount_other: '{{count}} Facets applied:',
        showLess: '$t(common:showLess)',
        clearAll: '$t(common:clearAll)'
    },
    FindDatasetsPage: {
        title: 'Find Datasets',
        previewUnavailable: 'Preview Unavailable',
        emptyPreview: 'Select a table or view to see a data preview.'
    },
    FindProjectsPage: {
        title: 'Find $t(glossary:project_other)',
        placeholder: 'Type to find a $t(glossary:project)…'
    },
    FlowDiagram: {
        empty: '<p>No data, no fun!<br/>Use the <PlusIcon/> in the Data Browser to</p><p><AddDataSourceButton>Add a Data Source</AddDataSourceButton> or <UploadButton>Upload a CSV</UploadButton></p>',
        actions: {
            search: 'Find nodes',
            showAll: 'Show all nodes',
            hideUnconnected: 'Hide unconnected nodes',
            hideUnconnectedDisabled: 'Select a node in the Project to enable this action',
            fit: 'Fit',
            center: 'Center on selected node',
            centerDisabled: 'Select a node in the Project to enable this action',
            zoomIn: 'Zoom in',
            zoomOut: 'Zoom out',
            pan: 'Pan',
            selection: 'Select'
        },
        indicators: {
            api: {
                active: 'Deployment triggered by API'
            },
            scheduled: {
                active: 'Deployment running on schedule: {{- repeat}}',
                paused: 'Deployment paused'
            },
            datadriven: {
                active: 'Deployment triggered by: {{- database}}.{{- schema}}.{{- table}}',
                paused: 'Deployment paused'
            },
            eventdriven: {
                active: 'Event-Driven deployment'
            },
            mail: {
                active: 'Results are emailed to {{recipients, list}} {{- repeat}}',
                paused: 'Email sharing paused'
            },
            hasProjection: 'Schema has been modified'
        },
        nodeProblemsCount: '{{count}} Problems'
    },
    ProjectTitleBar: {
        autorun: {
            label: 'Auto Preview Calculation',
            tooltip: 'Automatic calculation of data previews can be toggled ON/OFF for all datasets.'
        },
        version: {
            label: 'Version {{version}}',
            tooltip: 'View Full Project History'
        }
    },
    FQNInspectorSection: {
        copy: 'Copy FQN to clipboard'
    },
    Glossary: {
        search: 'Filter terms…',
        showTerm: 'Show term in $t(glossary:glossary)'
    },
    HealthCheckOverlay: {
        stackTraceToggle: 'Click for Stacktrace'
    },
    HelpMenu: {
        userGuide: 'User Manual',
        contactUs: 'Contact Us'
    },
    HomeButton: {
        title: 'List of Projects you have access to'
    },
    ImportingViewsSpinner: {
        message: '<p>Adding Datasets: <strong>{{importCount}} of {{importTotal}}</strong></p>'
    },
    Inspector: {
        selectAsset: 'Select an asset to inspect its properties.'
    },
    IntegrationUnconfiguredDialog: {
        title: 'Unconfigured Google Integration',
        goToSettings: 'Go to Settings…',
        configureGoogleIntegration: 'Configure your Google Sheet Integration settings from the administration page.',
        contactAdministrator: 'Contact your system administrator to configure Google Sheets Integration.'
    },
    LastRun: {
        refresh: 'Refresh Data',
        refreshed: 'Refreshed'
    },
    LicenseWarningOverlay: {
        message: 'Your Datameer license expires on August 31st, 2025. Please contact <a href="mailto:sales@datameer.com">sales@datameer.com</a> to avoid disruption of your service.'
    },
    ListInput: {
        removeItem: 'Remove'
    },
    ListItemSelect: {
        switchingTo: 'Switching to {{ itemInProgress }}'
    },
    MailSummaryInspector: {
        recipients: 'Recipients'
    },
    MaintenanceInfoButton: {
        button: {
            title: 'Maintenance Schedule'
        },
        dialog: {
            messageOk: 'Next maintenance is scheduled for <DateTime />',
            messageWarning: 'Attention. Maintenance happens within the next 24 hours. <DateTime />'
        }
    },
    ManageColumns: {
        brandbarTitle: 'Manage Columns',
        button_default: 'Manage Columns',
        button_modified: 'Manage Columns ({{included}}/{{total}})',
        modifiedColumnsCount: '{{count}} of {{total}} Modified',
        sourceColumnsCount: '{{total}} Source Columns',
        sourceDataset: 'Source Schema',
        outputDataset: 'Output Schema',
        duplicatedName: 'Column name already exists',
        everyColumnExcluded: 'Select at least one Column to see a preview.',
        modifications: {
            renamed: 'Renamed',
            typeChange: 'Type change',
            positionChange: 'Position change'
        }
    },
    MarkdownDocumentationLink: {
        label: 'Markdown supported'
    },
    MaterializeTableDialog: {
        title: 'Materialize Table',
        buttonDescription: 'Ad-hoc materializations will not affect the defined execution trigger.',
        alreadyRunning: 'There is already a table materialization running. <linkButton>Execution History</linkButton>'
    },
    MetadataGrid: {
        headers: {
            columnName: 'Column name',
            dataType: 'Data type',
            description: 'Description'
        }
    },
    NodeStatusDisclosure: {
        rollups: {
            error_one: '{{count}} Error',
            error_other: '{{count}} Errors',
            warning_one: '{{count}} Warning',
            warning_other: '{{count}} Warnings',
            info_one: '{{count}} Notification',
            info_other: '{{count}} Notifications',
            passed: '{{count}} Passed'
        },
        messages: {
            COLUMNS_ADDED_UPSTREAM: '<tag>Schema</tag> Column <identifier>{{- columnName}}</identifier> was added upstream.',
            COLUMNS_TYPE_CHANGED_UPSTREAM: '<tag>Schema</tag> The type of column <identifier>{{- columnName}}</identifier> changed from <parameter>{{typeBefore}}</parameter> to <parameter>{{typeAfter}}</parameter>.',
            COLUMNS_REMOVED_UPSTREAM: '<tag>Schema</tag> Column <identifier>{{- columnName}}</identifier> was removed upstream.',
            DUPLICATE_COLUMN_NAME: '<tag>Schema</tag> Duplicate Column <identifier>{{- columnName}}</identifier>',
            SCHEDULING_DAY: '<tag>Schedule Changed</tag> Deployment will now run every day at <parameter>{{startTime}}</parameter>.',
            SCHEDULING_WEEK: '<tag>Schedule Changed</tag> Deployment will now run every week on <parameter>{{repeats, list}}</parameter> at <parameter>{{startTime}}</parameter>.',
            SCHEDULING_MONTH: '<tag>Schedule Changed</tag> Deployment will now run every month on days <parameter>{{repeats, list}}</parameter> at <parameter>{{startTime}}</parameter>.',
            SCHEDULING_CUSTOM_CRON: '<tag>Schedule Changed</tag> Deployment will now run using custom cron pattern <parameter>{{- pattern}}</parameter>.',
            SCHEDULING_DISABLED: '<tag>Schedule Changed</tag> Scheduling got disabled in Snowflake.',
            TRANSFORMATION_SQL: '<tag>SQL Modified</tag> The resulting transformation SQL is out-of-sync with Snowflake.',
            FRESHNESS_CHECK_NOT_MET: '<tag>Quality Check</tag> Table <identifier>{{- database}}.{{- schema}}.{{- table}}</identifier> is more than <parameter>{{- duration}}</parameter> old.',
            MODEL_QUALITY_CHECK_NOT_MET: '<tag>Quality Check</tag> Table <identifier>{{- database}}.{{- schema}}.{{- table}}</identifier> failed its data quality checks. <job-run-link id="{{- jobRunId}}">View Job Run #{{jobRunId}} for details</job-run-link>.',
            INTERNAL_ERROR: '<tag>Internal Error</tag> {{- message}}',
            SNOWFLAKE_ERROR: '<tag>Snowflake Error</tag> {{- code}}: {{- message}}',
            ERROR_IN_REFERENCE: '<tag>Error in Reference</tag> {{- message}} <workbench-link space="{{- space}}" view="{{- view}}">Go to affected node</workbench-link>.',
            GENERIC_ERROR: '<tag>Error</tag> {{- message}}',
            GENERIC_WARNING: '<tag>Warning</tag> {{- message}}',
            [DUPLICATE_COLUMN_NAME]: '<tag>Schema</tag> Duplicate column name(s): {{ columnNames, list }}',
            [UNKNOWN_COLUMN_NAMES]: '<tag>Schema</tag> Following referenced columns are missing: {{columnNames, list}}',
            [UNKNOWN_COLUMN_NAMES_WITH_SOURCE]: '<tag>Schema</tag> Source dataset referencing unknown column(s): {{ columnNames, list }}',
            [MISSING_SOURCE_COLUMN]: '<tag>Schema</tag> Column "{{- columnName}}" contains a reference to a source column that does not exist in expression "{{- expression}}"',
            [COLUMN_REMOVED_UPSTREAM]: '<tag>Schema</tag> Column <identifier>{{- columnName}}</identifier> was removed upstream.',
            [EXPECTED_COLUMN_MISSING]: '<tag>Schema</tag> Column <identifier>{{- columnName}}</identifier> with type <identifier>{{expectedType}}</identifier> was removed upstream.',
            [TYPES_NOT_COERCIBLE]: '<tag>Schema</tag> Column <identifier>{{- columnName}}</identifier> cannot be coerced to <identifier>{{- sourceType}}</identifier>',
            [TYPE_COERCION]: '<tag>Schema</tag> The type of column <identifier>{{- columnName}}</identifier> changed from <parameter>{{-targetType}}</parameter> to <parameter>{{-sourceType}}</parameter>.',
            [TRANSFORMATION_OUT_OF_SYNC]: '<tag>SQL Modified</tag> The resulting transformation SQL is out-of-sync with Snowflake.',
            [COLUMN_ADDED_UPSTREAM]: '<tag>Schema</tag> Column <identifier>{{- columnName}}</identifier> was added upstream.',
            [MODEL_NOT_RESOLVED]: '<tag>Draft Model</tag> Resolve this draft by either discarding it or deploying it as a reusable model.',
            [MODEL_NOT_CONNECTED]: '<tag>Draft Model</tag> This draft must be connected to a data source (e.g. dataset, transformation) with compatible schema.',
            [HAS_DRAFT_MODEL_UPSTREAM]: '<tag>Draft Model</tag> There are 1 or more upstream draft models that must be resolved before this draft may be resolved.',
            [UNEXPECTED_COLUMN]: '<tag>Schema</tag>Column <identifier>{{- columnName}}</identifier> was added upstream.',
            qualityCheckErrors: {
                ExpectDateWithinPeriod: '<tag>Quality Check</tag> [{{target}}] <parameter>No update has occurred in at least {{duration}}</parameter> for <identifier>{{column}}</identifier>',
                ExpectAggregateWithinRange: '<tag>Quality Check</tag> [{{target}}] <parameter>Aggregate not within range [{{min}}, {{max}}]</parameter> for <identifier>{{column}}</identifier>',
                ExpectColumnNotNull: '<tag>Quality Check</tag> [{{target}}] <parameter>1 or more NULL values</parameter> found in <identifier>{{column}}</identifier>',
                ExpectAllValuesWithinRange: '<tag>Quality Check</tag> [{{target}}] <parameter>Not all values within range [{{min}}, {{max}}]</parameter> for <identifier>{{column}}</identifier>',
                ExpectRowCountWithinRange: '<tag>Quality Check</tag> [{{target}}] <parameter>Row count is not within range [{{min}}, {{max}}]</parameter>',
                ExpectUniqueColumnValues: '<tag>Quality Check</tag> [{{target}}] <parameter>Values are not unique</parameter> for <identifier>{{column}}</identifier>.',
                ExpectValuesMatchSet_Contains: '<tag>Quality Check</tag> [{{target}}] <parameter>Not all values in set [{{values}}]</parameter> are contained in <identifier>{{column}}</identifier>',
                ExpectValuesMatchSet_Contains_other: '<tag>Quality Check</tag> [{{target}}] <parameter>Not all values in set [{{values}}, +{{count}} more]</parameter> are contained in <identifier>{{column}}</identifier>',
                ExpectValuesMatchSet_ContainedBy: '<tag>Quality Check</tag> [{{target}}] <parameter>Not all column values contained in set [{{values}}]</parameter> for <identifier>{{column}}</identifier>',
                ExpectValuesMatchSet_ContainedBy_other: '<tag>Quality Check</tag> [{{target}}] <parameter>Not all column values contained in set [{{values}}, +{{count}} more]</parameter> for <identifier>{{column}}</identifier>',
                ExpectValuesMatchSet_Exact: '<tag>Quality Check</tag> [{{target}}] <parameter>Values do not equal set [{{values}}]</parameter> for <identifier>{{column}}</identifier>',
                ExpectValuesMatchSet_Exact_other: '<tag>Quality Check</tag> [{{target}}] <parameter>Values do not equal set [{{values}}, +{{count}} more]</parameter> for <identifier>{{column}}</identifier>'
            },
            qualityCheckInfos: {
                ExpectDateWithinPeriod: '<tag>Quality Check</tag> [{{target}}] <parameter>An update has occurred in at least {{duration}}</parameter> for <identifier>{{column}}</identifier>',
                ExpectAggregateWithinRange: '<tag>Quality Check</tag> [{{target}}] <parameter>Aggregate within range [{{min}}, {{max}}]</parameter> for <identifier>{{column}}</identifier>',
                ExpectColumnNotNull: '<tag>Quality Check</tag> [{{target}}] <parameter>No NULL values</parameter> found in <identifier>{{column}}</identifier>',
                ExpectAllValuesWithinRange: '<tag>Quality Check</tag> [{{target}}] <parameter>All values within range [{{min}}, {{max}}]</parameter> for <identifier>{{column}}</identifier>',
                ExpectRowCountWithinRange: '<tag>Quality Check</tag> [{{target}}] <parameter>Row count is within range [{{min}}, {{max}}]</parameter>',
                ExpectUniqueColumnValues: '<tag>Quality Check</tag> [{{target}}] <parameter>All values are unique</parameter> for <identifier>{{column}}</identifier>.',
                ExpectValuesMatchSet_Contains: '<tag>Quality Check</tag> [{{target}}] <parameter>All values in set [{{values}}]</parameter> are contained in <identifier>{{column}}</identifier>',
                ExpectValuesMatchSet_Contains_other: '<tag>Quality Check</tag> [{{target}}] <parameter>All values in set [{{values}}, +{{count}} more]</parameter> are contained in <identifier>{{column}}</identifier>',
                ExpectValuesMatchSet_ContainedBy: '<tag>Quality Check</tag> [{{target}}] <parameter>All column values contained in set [{{values}}]</parameter> for <identifier>{{column}}</identifier>',
                ExpectValuesMatchSet_ContainedBy_other: '<tag>Quality Check</tag> [{{target}}] <parameter>All column values contained in set [{{values}}, +{{count}} more]</parameter> for <identifier>{{column}}</identifier>',
                ExpectValuesMatchSet_Exact: '<tag>Quality Check</tag> [{{target}}] <parameter>Values equal set [{{values}}]</parameter> for <identifier>{{column}}</identifier>',
                ExpectValuesMatchSet_Exact_other: '<tag>Quality Check</tag> [{{target}}] <parameter>Values equal set [{{values}}, +{{count}} more]</parameter> for <identifier>{{column}}</identifier>'
            }
        }
    },
    NotificationHub: {
        clear: 'Clear',
        details: 'Show Details',
        detailsTitle: 'Error Details'
    },
    ProjectDeploymentPicker: {
        filter_placeholder: 'Filter projects…',
        version: 'Version {{version}} <message>({{message}})</message>',
        empty: 'Sorry, no projects found.',
        empty_filter: 'Sorry, no projects found matching <em>"{{term}}"</em>.'
    },
    ProjectDetailsPage: {
        inspector: {
            noAssets: 'Add dataset references to get started.',
            info: 'Info',
            history: 'History',
            referenceError: 'Transformations in this dataset have been impacted by one or more upstream errors.',
            datasetError: 'Transformations in this dataset have some errors.',
            affectedUpstreams: 'Affected upstream datasets:',
            showErrorDetails: 'Show Details',
            hideErrorDetails: 'Hide Details',
            missingColumns: 'Columns do not exist in predecessor schema:',
            transformationDisabled: 'Transformation has been disabled.'
        }
    },
    ProjectHistoryDialog: {
        title: 'Project History',
        columns: {
            version: 'Version',
            tags: 'Tags',
            createdAt: 'Timestamp',
            createdBy: 'Author',
            message: 'Message'
        },
        nodeNotExists: 'Node does not exist in the current version'
    },
    RecentsFlyout: {
        noRecent: "Let's get started with your first Project!",
        noOther: 'No other projects available, create a new one!',
        createNewProject: 'Create new project…',
        recents: 'Recents'
    },
    RecipientsList: {
        recipientsCount: '{{ count }} recipients'
    },
    RemoveAssetFromProjectDialog: {
        deleteAnyway: 'Delete anyway…',
        modelJobsWarning: 'Model has jobs outside the project environment that will be deleted.',
        warnings: {
            title: '$t(common:areYouSure)',
            reference: "This will remove the reference '{{- name}}' from this $t(glossary:project). You can add it back in again later.",
            operation: "This will permanently delete the dataset '{{- name}}'. No source data will be changed."
        },
        errors: {
            title: 'Sorry, not deletable!',
            GENERIC_ERROR: "'{{- name}}' cannot be deleted.",
            [MATERIALIZATION_CANNOT_BE_DELETED]: "Transformation node '{{-name}} is required by the deployed node and cannot be deleted.",
            [MULTI_SOURCE_CANNOT_BE_DELETED]: "'{{- name}}' cannot be deleted because it has multiple sources.",
            [SOURCE_CANNOT_BE_DELETED]: "'{{- name}}' cannot be removed because it provides data to one or more other datasets in $t(common:productName). Resolve all data dependencies and try again."
        }
    },
    RemoveUnusedDataSourcesDialog: {
        title: 'Are you sure?',
        message: 'This will remove all unused data sources. You can add them again later'
    },
    SampleExhaustion: {
        exhausted: 'Preview data has been exhausted',
        fullData: {
            button: 'Re-Calculate Preview',
            message: 'You also can <fulldata /> on full data.'
        },
        nodata: {
            transformation: 'Your transformation has resulted in no rows',
            exploration: 'Your query has resulted in no rows',
            datasource: 'Your dataset has resulted in no rows'
        },
        nodata_operation: "You can still work and try to configure this transformation, but won't be able to see any results.",
        nodata_explore: 'Preview data has been exhausted use <explore /> to find something specific you are looking for.',
        nodata_explore_and_calculate: 'Preview data has been exhausted see if there are any records with <rowcount /> or <explore /> to find something specific you are looking for.'
    },
    Scheduler: {
        title: 'Execution Trigger',
        type: 'Type',
        scheduled: 'Scheduled',
        config: 'On schedule',
        toggle: 'Enable Scheduler',
        adhoc: 'Table materialization occurs on an ad-hoc basis, as needed by the User.',
        jobAdHoc: 'Job runs occur on an ad-hoc basis, as needed by the User.',
        manual: 'Manual',
        repeat: 'Repeat',
        repeatOn: 'Repeat on',
        startAt: 'Start at',
        timeZone: 'Timezone',
        repeats: 'Repeats',
        lastRun: 'Last run',
        nextRun: 'Next run',
        WEEK: 'Every week on',
        DAY: 'Every day',
        CUSTOM_CRON: 'Using custom cron pattern',
        MONTH: 'Every month on days',
        daysInMonth: '1, 2, 3, 15, 31',
        daysInMonthError: 'Must be numbers (1-31) separated by commas',
        day30Hint: '<li>Schedule for <strong>day 30</strong> will skip for <strong>Feb</strong>.</li>',
        day31Hint: '<li>Schedule for <strong>day 31</strong> will skip for <strong>Feb, Apr, Jun, Sep, Nov</strong>.</li>',
        endOfMonthHint: 'Tip: for end of month, using <strong>day 1</strong> at <strong>12:00 AM</strong> is preferred. Alternatively, the special character "L" may be used in a custom cron pattern for the last day of the month.',
        pause: 'Pause',
        resume: 'Resume',
        edit: 'Edit',
        error: 'Error saving schedule.',
        day: 'Daily',
        week: 'Weekly',
        month: 'Monthly',
        custom: 'Custom...',
        utc: '(UTC)',
        cronPattern: 'Cron pattern',
        cronPatternError: 'Invalid cron pattern value',
        cronTable: {
            field: 'Required field',
            values: 'Allowed values',
            special: 'Special characters',
            minutes: 'Minutes',
            hours: 'Hours',
            monthDay: 'Day of month',
            month: 'Month',
            weekDay: 'Day of week'
        },
        status: {
            RUNNING: 'Running',
            ERROR: 'Failed',
            FAILED_WITH_ERROR: 'Failed'
        },
        notScheduled: 'Not scheduled',
        pending: 'Pending'
    },
    ScheduledSharesMenu: {
        sync: 'Sync Now'
    },
    ScheduleMailDialog: {
        configureScheduledEmail: 'Configure Scheduled Email',
        mailingSchedule: 'Mailing Schedule'
    },
    ScheduleSheetDialog: {
        configureTitle: 'Send to Google Sheets',
        googleSheetUrl: 'Google Sheet URL',
        schedule: 'Schedule',
        resumeTitle: '$t(common:areYouSure)',
        resumeMessage: 'This will resume the paused schedule. You can edit it later.',
        schemaMessage: {
            required: 'This field is required',
            validUrl: 'Please enter valid Google Sheet URL'
        }
    },
    SchemaConfiguration: {
        columnName: 'Column Name',
        columnType: 'Type',
        columnSample: 'Sample',
        loading: "We're calculating the preview of the newest file. This can take a moment."
    },
    SchemaUsage: {
        menu: 'Schema Usage',
        dialog: {
            title: 'Schema Usage',
            errorProjectUid: 'A Project must be selected',
            table: 'Table',
            addToProject: 'Add to Project',
            projectPlaceholder: 'Select project…'
        }
    },
    SearchInputWithAutocomplete: {
        warning: 'No matching values found.'
    },
    SearchInspector: {
        info: '$t(common:info)'
    },
    SearchFacets: {
        roleFacet: {
            header: '{{- userName}} is…'
        }
    },
    SearchResults: {
        suggestion: 'Here are your top {{count}} results. Add terms to find something more specific.'
    },
    SelectSnowflakeWarehouse: {
        label: 'Default Warehouse',
        defaultWarehouse: 'User Default ({{warehouse}})',
        placeholder: 'Select Default Warehouse…',
        xSmallError: 'The warehouse {{warehouse}} has the size X-Small. This can slow down the performance of this application.',
        noWarehouseError: 'There are no warehouses for the selected user. Please contact your admin or connect with a different user.',
        noDefaultWarehouse: 'There is no default warehouse configured for the selected user. Please select a warehouse to work with.',
        warehouseNotFound: 'Configured warehouse {{warehouse}} is not found in the list of available warehouses.'
    },
    SendMailNowDialog: {
        title: 'Send CSV via Email',
        subject: {
            title: 'Subject (Optional)'
        },
        message: {
            title: 'Message (Optional)'
        }
    },
    SetStatusCallout: {
        title: 'Set Status',
        placeholder: 'Find status…',
        count: 'Showing {{visible}} of {{total}} status tags'
    },
    ShareInsights: {
        export: 'Export…',
        title: 'Share a CSV version of this dataset',
        sendEmail: 'Send Email…',
        scheduleEmail: 'Schedule Email…',
        scheduleSheet: 'Send to Google Sheets…',
        recipients: 'Recipients',
        recipientsPlaceholder: 'Please enter an email address',
        validationErrors: {
            invalid_single: "Not a valid email: '{{- value}}'",
            invalid_multiple: 'Invalid email format',
            noRecipients: 'Recipient field must have at least 1 item'
        },
        download: 'Download CSV'
    },
    ShareScheduleInspector: {
        schedule: 'Schedule',
        paused: 'PAUSED',
        pauseSchedule: 'Pause',
        resumeSchedule: 'Resume',
        edit: 'Edit',
        sync: 'Sync Now'
    },
    ShareSourceInspector: {
        source: 'Source'
    },
    SheetSummaryInspector: {
        sheetUrl: 'Sheet URL'
    },
    Snackbar: {
        dismiss: 'Dismiss Notification'
    },
    SnackbarDetailsDialog: {
        title: {
            error: 'Error Details'
        }
    },
    SnowflakeBrowser: {
        title: '$t(common:dataBrowser)',
        tooltip: 'All of the Databases, Schemas and Datasets you have access to in your Snowflake account',
        inUse: 'In Use',
        scheduled: 'Scheduled',
        dataDriven: 'Data-Driven',
        api: 'Triggered by API',
        deployedFromDatameer: 'Deployed from Datameer',
        managedByDbt: 'Managed by DBT',
        actions: {
            add: '$t(common:addToProject)',
            remove: '$t(common:removeFromProject)',
            refreshDatabases: '$t(common:refresh) Databases and Schemas',
            refreshTables: '$t(common:refresh) Tables',
            refreshColumns: '$t(common:refresh) Columns'
        },
        filter: {
            [AssetFilter.ALL]: 'All Datasets',
            [AssetFilter.ADDED]: 'Added to Project',
            [AssetFilter.CREATED_BY_DATAMEER]: 'Created by Datameer',
            [AssetFilter.USED]: 'In Use',
            [AssetFilter.SCHEDULED]: 'Scheduled'
        },
        textFilter: {
            placeholder: 'Type to find a dataset…'
        },
        emptyMessage: '<div>This project does not contain any datasets yet.</div><div>To add a data source <action>Show all Datasets</action></div>',
        emptyMessageFiltered: '<div>The search returned no results.</div><div><action>Show all Datasets</action></div>',
        loadingMessage: 'Synchronizing Snowflake objects',
        noWarehouseError: 'Unable to list objects from Snowflake. Please <action>configure</action> a warehouse or contact your admin.',
        browserSandboxHint: '<p>Want to use your own Snowflake data?</p><linkButton>Connect to your account</linkButton>',
        unableToConnectToSnowflake: 'Data browsing capabilities are unavailable due to a connection issue with <linkButton>Snowflake</linkButton>.',
        filterButtonTitleEmpty: 'Search for a keyword to enable dataset filters',
        filterButtonTitle: 'Filter by…'
    },
    SnowflakeConnectionStatus: {
        connectedWithWarning: 'No warehouse selected. Please make sure that a default warehouse is defined for your Snowflake user.',
        status: {
            connected: 'CONNECTED',
            connectedNoWarehouse: 'CONNECTED (No warehouse selected)'
        },
        tooltip: {
            unconfiguredInfoAdmin: 'Configure your Snowflake settings from the administration page.',
            unconfiguredInfoNonAdmin: 'Please contact your Snowflake administrator for access.',
            state: 'Status:',
            account: 'Account:',
            user: 'User Name:',
            role: 'Role:',
            warehouse: 'Warehouse:',
            queries: 'Executing Queries:'
        },
        error: {
            [SECRET_NOT_FOUND]: 'Client secret missing. Specify a client secret in your Snowflake settings on the administration page.',
            [SNOWFLAKE_INTERNAL_SERVER_ERROR]: 'A Snowflake internal server error occurred. Contact your local system administrator.',
            NoHealthcheckReceived: 'No Snowflake connection status received, reach out to Datameer support.'
        },
        warning: {
            connectedWithWarning: 'No warehouse selected. Please make sure that a default warehouse is defined for your <linkButton>Snowflake</linkButton> user.'
        },
        modal: {
            signIn: 'Sign in to Snowflake',
            signOut: 'Sign out of Snowflake',
            unconfigured: {
                title: 'Unconfigured Snowflake Account',
                nonAdmin: 'Please contact your Snowflake administrator for access',
                admin: 'Configure your Snowflake settings from the administration page',
                adminButton: 'Setup Snowflake Account'
            },
            searchRolePlaceholder: 'Run as role…',
            searchWarehousePlaceholder: 'Run on warehouse…',
            noRoleSelected: 'Select a role to see available Warehouses'
        },
        list: {
            noMatchFound: 'Unfortunately, no match found for: <span>{{filter}}</span>'
        },
        warehouseStatus: {
            warehouseOverloaded: 'The warehouse may be overloaded.',
            running: 'Running queries:',
            queued: 'Queued queries:',
            state: 'State:',
            size: 'Size:'
        },
        showQueryOverview: '<linkButton>Click here</linkButton> to see the Query Warehouse Overview.',
        roleWarehouseDisabled: "Role and Warehouse values are bound to a Project's Environment.<br/> <linkButton>Click here</linkButton> to open the Job inspector to change them."
    },
    SnowflakeDisconnectedOverlay: {
        label: 'Sign in to your <linkButton>Snowflake account</linkButton> to experience the full capabilities of Datameer.',
        unconfiguredLabel: 'Unable to connect to Snowflake. <linkButton>Check your connection settings.</linkButton>',
        noStatus: 'No Snowflake connection status received. Please contact your administrator.'
    },
    SqlCodeViewer: {
        copySuccess: 'SQL query copied to clipboard'
    },
    StartUp: {
        errors: {
            keycloak: {
                title: 'Error communicating with Keycloak',
                message: 'We are not able to communicate with Keycloak. Please contact your System administrator.'
            }
        }
    },
    SummarizeCard: {
        actions: {
            delete: 'Delete',
            moveUp: 'Move Up',
            moveDown: 'Move Down'
        },
        RowConfig: {
            field: 'Field'
        },
        ColumnConfig: {
            field: 'Field',
            values: 'Values',
            count: '({{count}} values)',
            searchValues: 'Search for column values…',
            emptyValues: 'Please define values'
        },
        MeasureConfig: {
            field: 'Field',
            function: 'Aggregate Function',
            count: 'Count',
            countCheckboxLabel: 'Include row count (i.e. COUNT)',
            countDescription: 'Returns the number of rows. If row groupings have been applied, it returns the count for each group.'
        }
    },
    Table: {
        more: 'More actions'
    },
    Tag: {
        tooltip: "Search by '{{- value}}'",
        errorTooltip: "Invalid tag '{{- value}}'",
        buttonLabel: 'Remove Tag'
    },
    TagList: {
        tooltip: 'Add Tag',
        placeholderEmpty: 'Please provide tags',
        placeholderFilled: 'Start Typing'
    },
    Time: {
        now: 'Just now',
        ago: '{{amount}} ago'
    },
    TopK: {
        notShown: 'Not Shown:',
        rowsFrom: '{{ otherRowCount }} Rows from',
        terms: '{{ otherTermCount }} Terms'
    },
    TransformationStackEntry: {
        edit: 'Edit Operation',
        delete: 'Delete Operation'
    },
    TreeView: {
        loading: 'Loading items…',
        empty: 'Sorry, no items found.',
        more: 'More Actions'
    },
    UnsavedChangesWarningDialog: {
        title: 'Unsaved changes',
        message: 'You have unsaved changes. Are you sure you want to leave?',
        submit: 'Yes'
    },
    UploadDatasetDetailsDialog: {
        title: 'Upload Dataset',
        nameFieldLabel: 'Name',
        firstRowIsHeaderLabel: 'Use first row as column names',
        firstRowIsHeaderTooltip: 'When unchecked, Datameer will still attempt to determine if the first line of the file is actually a header row.',
        noFieldDelimiterLabel: 'Enforce single column',
        noFieldDelimiterTooltip: 'Enable this option if each line in the file should represent a single record in your dataset.',
        fileAttributes: 'File Attributes',
        submit: 'Upload',
        errors: {
            PayloadTooLarge: 'Oops! The upload was aborted due to an error. This error can occur if you try to upload a file bigger than 100 megabytes.'
        },
        overwrite: {
            title: 'Overwrite Table',
            body: 'Are you sure you want to overwrite the table? This cannot be undone.',
            button: 'Overwrite',
            hint: 'Press & Hold to Overwrite!'
        }
    },
    UploadDatasetFileDialog: {
        title: 'Upload CSV / Excel',
        addFileButton: 'Add File…',
        multiFileError: 'Multiple file upload is not supported.',
        placeholder: 'Drop file which you want to upload.',
        phrase: 'The more data the merrier!'
    },
    UploadDatasetWorkbookDialog: {
        title: 'Upload Excel Spreadsheet',
        preview: 'Preview',
        previewHint: 'Cell range may be by specified by clicking in the preview grid.',
        previewHint2: 'Complete the selection by clicking one more time.',
        sheet: 'Sheet',
        range: 'Range',
        rangeTooltip: 'Specify the rectangle to parse out of the selected sheet using standard notation like A1:G10, A:G, or A5:G. You can also click inside the preview to set the rectangle interactively.'
    },
    UsageDetailsInspector: {
        emptyState: {
            BarChart: 'Select a bar to see associated query details',
            LineChart: 'Select a data point to see associated queries'
        },
        exploreQueryDetails: 'Explore Query Details',
        selectionSummary: 'Selection Summary'
    },
    TagModelDialog: {
        tagMessage: 'Initial Deployment',
        tagNamePlaceholder: 'Enter a name for this Model Tag…'
    },
    ModelJobManagementPage: {
        title: '$t(common:model) Job Management'
    },
    CreateProjectEnv: {
        configure: 'Configure New Project',
        newProject: 'Create New Project',
        projectDetails: 'Project Details',
        projectEnv: 'Project Environment',
        envHint: 'If this project will deploy Models, choose or create an Environment to use during development. An Environment can also be added later in the Project Settings inspector.'
    },
    DeployModelDialog: {
        envWarning: 'You must select a Project Environment before you can deploy a model.',
        defaultMessage: 'Deploying model {{name}} on {{- date}}',
        convertToModelConfirmation: 'Are you sure you want to convert <strong>{{name}}</strong> to Model?',
        convertToModelWarning: 'Project Role and Warehouse do not match <strong>Role and Warehose</strong> used to deploy the table. Are you sure you want to convert <strong>{{name}}</strong> to Model?'
    },
    ProjectEnvironmentOverlay: {
        text: 'ERROR: The current Snowflake user cannot assume the role {{role}} needed to access the project. Please contact Snowflake administrator to adjust the role.'
    },
    JobTriggers: {
        info: 'By default jobs can always be triggered <b>manually</b> for ad-hoc execution. <br />Additional triggers types such as <b>event-driven</b>, or <b>scheduled</b> job execution are also supported.',
        unavailableJobsWarning: 'One or more jobs are not available.',
        deployedTableTrigger: 'Deployed Table Trigger',
        jobTrigger: 'Job Trigger',
        runWheneverJobExecuted: 'Run whenever {{type}} is executed…',
        runWheneverTableDeployed: 'Run whenever table is deployed…',
        jobTriggerError_deleted: 'The selected Job has been deleted. Please delete the Trigger.',
        jobTriggerError_unavailable: 'The selected Job is not available in the current Environment. Please delete the Trigger.',
        forJob: 'For Job',
        forTable: 'For Table'
    },
    WarehouseCard: {
        name: 'Name',
        state: 'State',
        running: 'Running',
        queued: 'Queued',
        size: 'Size'
    },
    WarehouseLoadDialog: {
        menu: 'Query Overview (Running {{executing}}/{{total}})',
        warehouse: 'Warehouse',
        status: 'Status',
        actions: 'Recommended Actions',
        chart: {
            title: 'Query Workload Overview',
            warning: '<b>{{warehouse}}</b> has reached the maximum number of concurrent queries and has begun to queue new query requests.  New queries from your session will wait in the queue and will not be executed immediately. See below for our recommended solutions, or contact your Snowflake administrator for further assistance.',
            empty: '<h>Warehouse health check in progress…</h><p>Status information will be visualized here when<br />it becomes available.</p>'
        },
        queries: {
            cta: 'Manage My Queries',
            description: 'Help reduce the overload!<br />You have <b>{{running}} running</b> and <b>{{queued}} queued</b> queries.',
            hint: 'Cancelling your queries will help free up warehouse resources for other affected users.',
            header: 'Improve warehouse performance by managing your queries:',
            back: 'Back to Overview',
            cancel: '$t(common:cancel)',
            sql: 'Show Query SQL',
            cancelAll: '$t(common:cancelAllQueries)',
            columns: {
                id: 'ID',
                status: 'Status',
                context: 'Context',
                started: 'Started At',
                duration: 'Duration'
            }
        },
        warehouses: {
            cta: '$t(common:changeWarehouse)',
            description: 'You have access to <b>{{warehouses}}</b> other warehouses.',
            hint: 'Consider a different warehouse with more available capacity to perform your query.',
            header: 'Select a warehouse for your queries:',
            back: 'Back to Overview'
        }
    },
    QualityChecks: {
        createNewCheck: 'Create New Check…',
        findCheck: 'Find quality check…',
        noResults: 'No results available.',
        moreItems: '+{{count}} more',
        filter: {
            label: 'Filter on:',
            count_zero: 'No results',
            count_one: '1 result',
            count_other: '{{count}} results',
            options: {
                sources: 'Source Tables',
                model: 'Model'
            }
        },
        columns: {
            target: 'Target',
            checkName: 'Check',
            params: 'Params',
            enabled: '$t(common:enabled)'
        },
        checks: {
            ExpectDateWithinPeriod: 'Dataset Freshness',
            ExpectAggregateWithinRange: 'Aggregate In Range',
            ExpectColumnNotNull: 'Not Null',
            ExpectAllValuesWithinRange: 'All Values within Range',
            ExpectValuesMatchSet: 'Values Match Set',
            ExpectRowCountWithinRange: 'Row Count within Range',
            ExpectUniqueColumnValues: 'Unique Column Values'
        }
    },
    ProblemsPanel: {
        title: 'Problems',
        success: "All systems go - looks like you're problem free!",
        nodesListEmptyState: 'Any future errors or warnings relating to this project or its nodes will be shown here.',
        clearFilters: 'Clear Filters',
        filterOnSelection: 'Filter on Current Selection',
        errorLevel: {
            warnings: 'Warnings',
            errors: 'Errors',
            warningsCount: 'Warnings ({{count}})',
            errorsCount: 'Errors ({{count}})',
            all: 'All'
        },
        errorTypes: {
            structural: 'Structural ({{count}})',
            dataQuality: 'Data Quality ({{count}})',
            compilation: 'Compilation ({{count}})'
        },
        popoutPanel: 'Pop-out Problems Panel',
        dockPanel: 'Dock Problems Panel'
    }
};
